import {Trans} from '@lingui/macro';
import React, {FC} from 'react';
import styled from 'styled-components';

import {useInitialEffect} from '../../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {HandlerOf} from '../../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../../libs/shared/helpers/src/types/asyncStatuses';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {AsyncLoader} from '../../../../components/common/loaders/asyncLoader';
import {useLinkNavigation} from '../../../../components/common/navigation/useLinkNavigation';
import {Skeleton} from '../../../../components/common/skeleton';
import {useAppDispatch} from '../../../../helpers/reduxHelpers';
import {LocalApplicationModel} from '../../../../models/api/localApplicationModel';
import {buildSettingsDevelopersPath} from '../../../../models/url/settingsUrlHelpers';
import {
  SettingsDevelopersApplicationFeaturesUrl,
  SettingsDevelopersApplicationInfoUrl,
  SettingsDevelopersApplicationOAuthUrl,
  SettingsDevelopersApplicationServersUrl,
  SettingsDevelopersPagesEnum,
  SettingsRootPagesEnum,
} from '../../../../models/url/settingsUrlModel';
import {SettingsPanelId} from '../../../../store/panels/panelStateBase';
import {fetchLocalApplicationForSettingsAsyncAction} from '../../../../store/panels/settings/settingsPanelActions';
import {SettingsDevelopersApplicationFeaturesPage} from './features/settingsDevelopersApplicationFeaturesPage';
import {SettingsDevelopersApplicationInfoPage} from './info/settingsDevelopersApplicationInfoPage';
import {SettingsDevelopersApplicationOAuthPage} from './oauth/settingsDevelopersApplicationOAuthPage';
import {SettingsDevelopersApplicationServersPage} from './servers/settingsDevelopersApplicationServersPage';

/*
 * Props.
 */

interface SettingsDevelopersApplicationPageProps {
  panelId: SettingsPanelId;
  url:
    | SettingsDevelopersApplicationFeaturesUrl
    | SettingsDevelopersApplicationInfoUrl
    | SettingsDevelopersApplicationServersUrl
    | SettingsDevelopersApplicationOAuthUrl;
  isApplicationLoading: boolean;
  application?: LocalApplicationModel;
  asyncStatus?: AsyncStatusesEnum;
}

/*
 * Style.
 */

const StyledEmptyStateWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  padding: 64px;
  gap: 32px;
  text-align: center;
  flex: 1;
`;

const StyledButtonWrapperDiv = styled.div`
  display: flex;
  align-self: center;
`;

const StyledErrorWrapperDiv = styled.div`
  flex: 1;
  padding: 32px;
`;

/*
 * Component.
 */

export const SettingsDevelopersApplicationPage: FC<SettingsDevelopersApplicationPageProps> = (props) => {
  const {panelId, url, asyncStatus, isApplicationLoading, application} = props;
  const dispatch = useAppDispatch();
  const {navigateToLink} = useLinkNavigation();

  useInitialEffect(() => {
    dispatch(fetchLocalApplicationForSettingsAsyncAction(panelId, url.applicationId));
  });

  // TODO: Flesh out the loading ui better.
  if (isApplicationLoading) {
    return <Skeleton height="300px" width="600px" />;
  }
  if (asyncStatus === AsyncStatusesEnum.FAILED) {
    return (
      <StyledErrorWrapperDiv>
        <AsyncLoader
          status={asyncStatus}
          onRetryClick={() =>
            dispatch(fetchLocalApplicationForSettingsAsyncAction(panelId, url.applicationId))
          }
        />
      </StyledErrorWrapperDiv>
    );
  }

  // Empty state for if we fail to find an application.
  if (!application) {
    return renderEmptyState(navigateToLink);
  }

  switch (url.subPage) {
    case SettingsDevelopersPagesEnum.APPLICATION_FEATURES:
      return <SettingsDevelopersApplicationFeaturesPage url={url} application={application} />;
    case SettingsDevelopersPagesEnum.APPLICATION_OAUTH:
      return <SettingsDevelopersApplicationOAuthPage url={url} application={application} />;
    case SettingsDevelopersPagesEnum.APPLICATION_SERVERS:
      return <SettingsDevelopersApplicationServersPage url={url} />;
    case SettingsDevelopersPagesEnum.APPLICATION_INFO:
    default:
      return <SettingsDevelopersApplicationInfoPage url={url} application={application} />;
  }
};

/*
 * Helpers.
 */

function renderEmptyState(navigateToLink: HandlerOf<string>) {
  const onButtonClick = () => {
    navigateToLink(
      buildSettingsDevelopersPath({
        rootPage: SettingsRootPagesEnum.DEVELOPERS,
        subPage: SettingsDevelopersPagesEnum.APPLICATIONS,
      }),
    );
  };

  return (
    <StyledEmptyStateWrapperDiv>
      <Trans id="settings-developers-details-empty-state">We are not able to find this app.</Trans>
      <StyledButtonWrapperDiv>
        <Button variant={ButtonVariantsEnum.PRIMARY} onClick={onButtonClick}>
          <Trans id="settings-developers-details-empty-state-button">Back</Trans>
        </Button>
      </StyledButtonWrapperDiv>
    </StyledEmptyStateWrapperDiv>
  );
}
